import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Alert,
  IconButton,
  Chip,
  CircularProgress,
  Autocomplete,
  Rating,
  Switch,
} from '@mui/material';
import {
  PersonAddOutlined,
  EditOutlined,
  VisibilityOutlined,
  AddCircleOutline,
  RemoveCircleOutline,
  CheckCircle,
  Cancel,
  Star,
  StarBorder,
  EventRepeat,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { INDIA_STATES, getDistrictsByState } from '../../constants/indiaLocations';
import { useNotification } from '../../contexts/NotificationContext';

interface CustomerFormProps {
  mode: 'create' | 'edit' | 'view';
}

const CustomerForm: React.FC<CustomerFormProps> = ({ mode }) => {
  const isViewMode = mode === 'view';
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { alert } = useNotification();

  // Form state
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [state, setState] = useState('');
  const [transportPartners, setTransportPartners] = useState<string[]>(['']);
  const [rating, setRating] = useState<number | null>(null);
  const [orderCycleDays, setOrderCycleDays] = useState<number | ''>('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch customer details in edit/view mode
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && id) {
      const fetchCustomer = async () => {
        setFetching(true);
        try {
          const response = await api.get(`/api/partners/${id}`);
          const customer = response.data.partner || response.data;
          setName(customer.name || '');
          setCity(customer.city || '');
          setDistrict(customer.district || '');
          setState(customer.state || '');
          const transports = customer.metadata?.preferred_transports || [];
          setTransportPartners(transports.length > 0 ? transports : ['']);
          setRating(customer.rating ?? null);
          setOrderCycleDays(customer.order_cycle_days ?? '');
          setIsEnabled(customer.is_enabled ?? true);
        } catch (err) {
          console.error('Error fetching customer:', err);
          setError('Failed to load customer details');
        } finally {
          setFetching(false);
        }
      };
      fetchCustomer();
    }
  }, [mode, id]);

  // Transport Partners handlers
  const handleTransportChange = (index: number, value: string) => {
    const updated = [...transportPartners];
    updated[index] = value;
    setTransportPartners(updated);
  };

  const addTransportPartner = () => {
    setTransportPartners([...transportPartners, '']);
  };

  const removeTransportPartner = (index: number) => {
    if (transportPartners.length === 1) {
      setTransportPartners(['']);
      return;
    }
    const updated = transportPartners.filter((_, i) => i !== index);
    setTransportPartners(updated);
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError('Customer name is required');
      return false;
    }
    if (!city.trim()) {
      setError('City is required');
      return false;
    }
    if (!district.trim()) {
      setError('District is required');
      return false;
    }
    if (!state.trim()) {
      setError('State is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      type: 'CUSTOMER' as const,
      name: name.trim(),
      city: city.trim(),
      district: district.trim(),
      state: state.trim(),
      rating: rating,
      order_cycle_days: orderCycleDays === '' ? null : orderCycleDays,
      is_enabled: isEnabled,
      metadata: {
        preferred_transports: transportPartners
          .map((p) => p.trim())
          .filter((p) => p.length > 0),
      },
    };

    try {
      if (mode === 'create') {
        const response = await api.post('/api/partners', payload);
        const customerId = response.data?.id || response.data?.partner?.id;
        const customerName = name.trim();
        // Reset form
        setName('');
        setCity('');
        setDistrict('');
        setState('');
        setTransportPartners(['']);
        setRating(null);
        setOrderCycleDays('');
        setIsEnabled(true);
        // Show success dialog
        await alert({
          title: 'Customer Created',
          message: `${customerName} (#${customerId || ''}) created successfully!`,
          icon: 'success',
        });
      } else {
        await api.put(`/api/partners/${id}`, payload);
        navigate(-1);
      }
    } catch (err) {
      setError('Failed to save customer');
      console.error('Error saving customer:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {mode === 'create' ? (
          <PersonAddOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        ) : mode === 'view' ? (
          <VisibilityOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        ) : (
          <EditOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        )}
        <Typography variant="h5" fontWeight="bold">
          {mode === 'create' ? 'Add New Customer' : mode === 'view' ? 'Customer Details' : 'Edit Customer'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Name */}
        <TextField
          fullWidth
          label="Customer Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
          required
          placeholder="Enter customer name"
          disabled={isViewMode}
          InputProps={{ readOnly: isViewMode }}
        />

        {/* Customer Rating */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: rating ? 'warning.50' : 'grey.50',
            border: '1px solid',
            borderColor: rating ? 'warning.200' : 'grey.200',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {rating ? (
                <Star sx={{ color: 'warning.main', fontSize: 28 }} />
              ) : (
                <StarBorder sx={{ color: 'text.disabled', fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  Customer Rating
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rating ? `Rated ${rating} out of 5 stars` : 'No rating assigned'}
                </Typography>
              </Box>
            </Box>
            <Rating
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
              size="large"
              readOnly={isViewMode}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: 'warning.main',
                },
                '& .MuiRating-iconHover': {
                  color: 'warning.dark',
                },
              }}
            />
          </Box>
        </Box>

        {/* Order Cycle Days */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: orderCycleDays ? 'info.50' : 'grey.50',
            border: '1px solid',
            borderColor: orderCycleDays ? 'info.200' : 'grey.200',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EventRepeat sx={{ color: orderCycleDays ? 'info.main' : 'text.disabled', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  Order Cycle Days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {orderCycleDays ? `Orders every ${orderCycleDays} days` : 'No cycle set'}
                </Typography>
              </Box>
            </Box>
            <TextField
              type="number"
              size="small"
              value={orderCycleDays}
              onChange={(e) => setOrderCycleDays(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Days"
              disabled={isViewMode}
              inputProps={{ min: 0, style: { width: 80, textAlign: 'center' }, readOnly: isViewMode }}
              sx={{ width: 100 }}
            />
          </Box>
        </Box>

        {/* Enable/Disable Customer - Only in Edit/View Mode */}
        {(mode === 'edit' || mode === 'view') && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: isEnabled ? 'success.50' : 'grey.100',
              border: '1px solid',
              borderColor: isEnabled ? 'success.200' : 'grey.300',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isEnabled ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
              ) : (
                <Cancel sx={{ color: 'text.disabled', fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {isEnabled ? 'Customer Active' : 'Customer Inactive'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isEnabled ? 'This customer is active and visible' : 'This customer is hidden from listings'}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              disabled={isViewMode}
              color="success"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'success.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'success.main',
                },
              }}
            />
          </Box>
        )}

        {/* State */}
        <Autocomplete
          fullWidth
          options={INDIA_STATES}
          value={state || null}
          onChange={(_, newValue) => {
            setState(newValue || '');
            // Clear district when state changes
            setDistrict('');
          }}
          disabled={isViewMode}
          readOnly={isViewMode}
          renderInput={(params) => (
            <TextField
              {...params}
              label="State"
              required
              placeholder="Search state..."
            />
          )}
          sx={{ mb: 3 }}
        />

        {/* District */}
        <Autocomplete
          fullWidth
          options={state ? getDistrictsByState(state) : []}
          value={district || null}
          onChange={(_, newValue) => setDistrict(newValue || '')}
          disabled={!state || isViewMode}
          readOnly={isViewMode}
          renderInput={(params) => (
            <TextField
              {...params}
              label="District"
              required
              placeholder={state ? "Search district..." : "Select state first"}
            />
          )}
          sx={{ mb: 3 }}
          noOptionsText={state ? "No districts found" : "Select a state first"}
        />

        {/* City */}
        <TextField
          fullWidth
          label="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          sx={{ mb: 3 }}
          required
          placeholder="Enter city"
          disabled={isViewMode}
          InputProps={{ readOnly: isViewMode }}
        />

        {/* Preferred Transport Partners */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Preferred Transport Partners <Typography component="span" variant="caption" color="text.disabled">(Optional)</Typography>
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            {transportPartners.map((partner, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: index < transportPartners.length - 1 ? 1.5 : 0,
                }}
              >
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{ minWidth: 28 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={partner}
                  onChange={(e) => handleTransportChange(index, e.target.value)}
                  placeholder={`Transport Partner ${index + 1}`}
                  sx={{ bgcolor: 'white' }}
                  disabled={isViewMode}
                  InputProps={{ readOnly: isViewMode }}
                />
                {!isViewMode && (
                  <IconButton
                    color="error"
                    onClick={() => removeTransportPartner(index)}
                    size="small"
                    title="Remove"
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                )}
              </Box>
            ))}
            {!isViewMode && (
              <Button
                startIcon={<AddCircleOutline />}
                onClick={addTransportPartner}
                variant="text"
                size="small"
                sx={{ mt: 1.5 }}
              >
                Add Transport Partner
              </Button>
            )}
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isViewMode && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading
                ? 'Saving...'
                : mode === 'create'
                ? 'Add Customer'
                : 'Update Customer'}
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/customers')}>
            {isViewMode ? 'Back' : 'Cancel'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CustomerForm;
